import { Navigate } from "react-router";
import { holdsModule } from "../lib/access";
import { DestinationEntrance } from "./application-route-helpers";
import type { AppScreen } from "./application-route-types";
import { lazyScreen } from "./lazy-screen";

const Assets = lazyScreen(() => import("./assets"), "Assets");
const Declarations = lazyScreen(() => import("./declarations"), "Declarations");
const Documents = lazyScreen(() => import("./documents"), "Documents");
const Joining = lazyScreen(() => import("./joining"), "Joining");
const OffboardingTasks = lazyScreen(() => import("./offboarding-tasks"), "OffboardingTasks");
const Offboarding = lazyScreen(() => import("./offboarding"), "Offboarding");
const OnboardingForms = lazyScreen(() => import("./onboarding-forms"), "OnboardingForms");
const Onboarding = lazyScreen(() => import("./onboarding"), "Onboarding");
const OnboardingSupport = lazyScreen(() => import("./onboarding-support"), "OnboardingSupport");
const OnboardingAlignment = lazyScreen(
  () => import("./onboarding-alignment"),
  "OnboardingAlignment",
);
const OnboardingDevelopment = lazyScreen(
  () => import("./onboarding-development"),
  "OnboardingDevelopment",
);
const OnboardingFinalReview = lazyScreen(
  () => import("./onboarding-final-review"),
  "OnboardingFinalReview",
);
const OnboardingDocumentRequests = lazyScreen(
  () => import("./onboarding-candidate"),
  "OnboardingDocumentRequests",
);
const OnboardingSafetyPrograms = lazyScreen(
  () => import("./onboarding-safety"),
  "OnboardingSafetyPrograms",
);
const OnboardingSafetyAssessments = lazyScreen(
  () => import("./onboarding-safety"),
  "OnboardingSafetyAssessments",
);
const OnboardingSafetyStanding = lazyScreen(
  () => import("./onboarding-safety"),
  "OnboardingSafetyStanding",
);
const Payroll = lazyScreen(() => import("./payroll"), "Payroll");
const RequisitionReports = lazyScreen(() => import("./requisition-reports"), "RequisitionReports");
const RecruitingCalendar = lazyScreen(() => import("./calendar"), "CalendarWorkspace");
const RecruitingProcessPlans = lazyScreen(
  () => import("./recruiting-process-plans"),
  "RecruitingProcessPlans",
);
const Recruiting = lazyScreen(() => import("./recruiting"), "Recruiting");
const Retention = lazyScreen(() => import("./retention"), "Retention");
const WorkforcePlanning = lazyScreen(() => import("./planning"), "WorkforcePlanning");
const WorkforceScenarioCreate = lazyScreen(() => import("./planning"), "WorkforceScenarioCreate");
const WorkforceScenarioDetail = lazyScreen(() => import("./planning"), "WorkforceScenarioDetail");
const WorkforceScenarioRevision = lazyScreen(
  () => import("./planning"),
  "WorkforceScenarioRevision",
);

export const OPERATIONS_SCREENS: readonly AppScreen[] = [
  // Routed even without the role, though the link is hidden: an address somebody was SENT
  // should reach riksu and be refused by riksu, with the sentence riksu wrote. Dropping
  // the route as well would answer a real address with "no such page", which sends
  // somebody hunting a typo.
  // THE ROLES GO IN and the PRINCIPAL does not, which is the opposite of `/time` above and
  // for a reason worth stating: a payroll period is about an EMPLOYER, not about a person,
  // so there is nobody for this screen to open on. What it needs to know is whether the
  // caller holds a payroll control, because every one of the five routes takes one — the
  // screen refuses politely rather than rendering four controls riksu will refuse.
  //
  // Routed without the role all the same, like the three below it: an address somebody was
  // SENT should reach riksu and be refused by riksu.
  {
    path: "/payroll",
    render: ({ state }) => <DestinationEntrance at="/payroll" state={state} />,
  },
  {
    path: "/payroll/open",
    render: ({ state }) => <Payroll roles={state.riksu.roles ?? []} />,
  },
  // THE COMPLETED MONTHS, as a second entry rather than as `/payroll/:section` alone —
  // the same pair `/assets` takes and for the same reason: `ROUTE_PATHS` is what the
  // trail gate walks, and a single parameterised route would leave `/payroll` itself
  // unwalked, which is the address somebody actually lands on.
  {
    path: "/payroll/:section",
    render: ({ state }) => <Payroll roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/inbox/tasks/onboarding",
    render: () => <Navigate to="/inbox/tasks/onboarding/support" replace />,
  },
  {
    path: "/inbox/tasks/onboarding/support",
    render: ({ state }) => (
      <OnboardingSupport roles={state.riksu.roles ?? []} workspace="participant" />
    ),
  },
  {
    path: "/inbox/tasks/onboarding/support/:plan_id",
    render: ({ state }) => (
      <OnboardingSupport roles={state.riksu.roles ?? []} workspace="participant" />
    ),
  },
  {
    path: "/inbox/tasks/onboarding/support/:plan_id/alignment",
    render: () => <OnboardingAlignment workspace="participant" />,
  },
  {
    path: "/inbox/tasks/onboarding/reviews",
    render: () => <OnboardingDevelopment workspace="participant" />,
  },
  {
    path: "/inbox/tasks/onboarding/reviews/:review_id",
    render: () => <OnboardingDevelopment workspace="participant" />,
  },
  {
    path: "/inbox/tasks/onboarding/reviews/for-plan/:plan_id",
    render: () => <OnboardingDevelopment workspace="participant" />,
  },
  {
    path: "/inbox/tasks/onboarding/final-reviews",
    render: () => <OnboardingFinalReview workspace="participant" />,
  },
  {
    path: "/inbox/tasks/onboarding/final-reviews/:review_id",
    render: () => <OnboardingFinalReview workspace="participant" />,
  },
  {
    path: "/inbox/tasks/onboarding/final-reviews/for-plan/:plan_id",
    render: () => <OnboardingFinalReview workspace="participant" />,
  },
  {
    path: "/onboarding",
    render: ({ state }) => <DestinationEntrance at="/onboarding" state={state} />,
  },
  {
    path: "/onboarding/documents",
    render: ({ state }) => <OnboardingDocumentRequests issuer={state.riksu.issuer} />,
  },
  {
    path: "/onboarding/documents/:request_id",
    render: ({ state }) => <OnboardingDocumentRequests issuer={state.riksu.issuer} />,
  },
  { path: "/onboarding/safety/programs", render: () => <OnboardingSafetyPrograms /> },
  { path: "/onboarding/safety/programs/:program_id", render: () => <OnboardingSafetyPrograms /> },
  { path: "/onboarding/safety/assessments", render: () => <OnboardingSafetyAssessments /> },
  {
    path: "/onboarding/safety/assessments/:assessment_id",
    render: () => <OnboardingSafetyAssessments />,
  },
  {
    path: "/onboarding/safety/employments/:employment_id",
    render: () => <OnboardingSafetyStanding />,
  },
  { path: "/onboarding/forms", render: ({ state }) => <OnboardingForms held={state.riksu} /> },
  { path: "/onboarding/joiners", render: () => <Onboarding /> },
  { path: "/onboarding/reviews", render: () => <OnboardingDevelopment /> },
  { path: "/onboarding/reviews/:review_id", render: () => <OnboardingDevelopment /> },
  { path: "/onboarding/reviews/for-plan/:plan_id", render: () => <OnboardingDevelopment /> },
  { path: "/onboarding/final-reviews", render: () => <OnboardingFinalReview /> },
  { path: "/onboarding/final-reviews/:review_id", render: () => <OnboardingFinalReview /> },
  { path: "/onboarding/final-reviews/for-plan/:plan_id", render: () => <OnboardingFinalReview /> },
  {
    path: "/onboarding/support/:plan_id/alignment",
    render: () => <OnboardingAlignment />,
  },
  {
    path: "/onboarding/support",
    render: ({ state }) => <OnboardingSupport roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/onboarding/support/:plan_id",
    render: ({ state }) => <OnboardingSupport roles={state.riksu.roles ?? []} />,
  },
  // THE LEDGER, AS ITS OWN ADDRESS. The module is two natures — who is joining, and what
  // the company owes the state — and it drew both on one screen while its rail entry named
  // only the second. A second entry rather than `/onboarding/:section` for the reason
  // `/retention`, `/payroll` and `/assets` all take: `ROUTE_PATHS` is what the trail gate
  // walks, and one parameterised route leaves `/onboarding` itself unwalked, which is the
  // address somebody actually lands on.
  { path: "/onboarding/deadlines", render: () => <Onboarding /> },
  // THE REGISTER OF DECLARATIONS MADE. Its own address for the same reason the ledger has
  // one, and it takes the reader's own person rather than their roles: the screen refuses
  // nobody — riksu filters the list — and the one thing it does per caller is mark which
  // row is theirs. `null` is ordinary here, for a principal riksu cannot place.
  {
    path: "/onboarding/declarations",
    render: ({ state }) => <Declarations personId={state.riksu.person?.person_id ?? null} />,
  },
  // ONE JOINING, WALKED. The step is in the PATH for the reason `/setup/:step` is: it is
  // what a bookmark keeps, what somebody pastes into a message, and what makes the
  // browser's Back button move between steps the way it moves between anything else.
  //
  // The bare `/onboarding/joining/:employment_id` is a second entry rather than an
  // optional segment, the same pair `/assets` and `/assets/:section` are — `ROUTE_PATHS`
  // is what the trail gate walks, and it is also the honest landing address for somebody
  // who has not chosen a step yet: the wizard PROPOSES one there and puts nothing in the
  // address until they press.
  //
  // NOT A `RecordAddress`, and that is the distinction to keep. A record is a pane drawn
  // over the screen it was pressed on and it holds a READ; this is a place somebody works
  // in for ten minutes and comes back to, and `useDiscardGuard` exists to hold a form
  // AGAINST a navigation rather than inside one.
  {
    path: "/onboarding/joining/:employment_id",
    render: () => <Joining />,
  },
  {
    path: "/onboarding/joining/:employment_id/:step",
    render: () => <Joining />,
  },
  { path: "/offboarding", render: () => <Offboarding /> },
  {
    path: "/inbox/tasks/offboarding",
    render: ({ state }) => <OffboardingTasks roles={state.riksu.roles ?? []} />,
  },
  // RECRUITING. The ROLES go in for the reason they go into `/payroll`, `/assets` and
  // `/retention`: every route in the module is `Subject::Role`, so the screen refuses
  // politely rather than drawing controls riksu will refuse — and this module needs the
  // roles for a second reason the other three do not have. It is gated by TWO server sets,
  // so the screen decides per SECTION which refusal it is rendering: a finance approver
  // reaching `/recruiting` holds the seat half and not the pipeline, and "this is not
  // yours" about the pipeline is a different sentence from "you cannot reach recruiting".
  //
  // The bare path and the section path are two entries rather than one, the pair `/assets`,
  // `/payroll` and `/retention` all take: `ROUTE_PATHS` is what the trail gate walks, and a
  // single `/recruiting/:section` would leave `/recruiting` itself unwalked — which is the
  // address somebody actually lands on.
  {
    path: "/planning/scenarios",
    render: ({ state }) => <WorkforcePlanning roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/planning/scenarios/new",
    render: ({ state }) => <WorkforceScenarioCreate roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/planning/scenarios/:scenario_id",
    render: ({ state }) => <WorkforceScenarioDetail roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/planning/scenarios/:scenario_id/revise",
    render: ({ state }) => <WorkforceScenarioRevision roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/recruiting",
    render: ({ state }) => <DestinationEntrance at="/recruiting" state={state} />,
  },
  {
    path: "/recruiting/interviews",
    render: () => <RecruitingCalendar purpose="recruiting" />,
  },
  {
    path: "/recruiting/applications",
    render: ({ state }) => <Recruiting roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/recruiting/requisitions/reports",
    render: ({ state }) => <RequisitionReports roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/recruiting/requisitions/new",
    render: ({ state }) => <Recruiting roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/recruiting/process-plans",
    render: ({ state }) => <RecruitingProcessPlans roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/recruiting/:section",
    render: ({ state }) => <Recruiting roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/recruiting/candidates/:person_id",
    render: ({ state }) => <Recruiting roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/recruiting/careers/:publication_id",
    render: ({ state }) => <Recruiting roles={state.riksu.roles ?? []} />,
  },
  // THE ZIMMET, for the same reason as the two above: routed even without the role,
  // though the rail link is hidden. The bare path and the section path are two entries
  // rather than one, because `ROUTE_PATHS` is what the trail gate walks and a single
  // `/assets/:section` would leave `/assets` itself unwalked — which is the address
  // somebody actually lands on.
  {
    path: "/assets",
    render: ({ state }) => <DestinationEntrance at="/assets" state={state} />,
  },
  {
    path: "/assets/holdings",
    render: ({ state }) => <Assets roles={state.riksu.roles ?? []} />,
  },
  {
    path: "/assets/:section",
    render: ({ state }) => <Assets roles={state.riksu.roles ?? []} />,
  },
  // THE DOCUMENT REGISTER. The ROLES go in and the PRINCIPAL does not, and the reasoning is
  // the OPPOSITE of `/payroll`'s on both halves — worth stating, because copying either
  // neighbour's sentence would be wrong here.
  //
  // The roles are not because riksu will refuse the reads: neither chase list takes a role,
  // and a caller holding none is NARROWED to their own subtree rather than refused. They are
  // because a screen with no control on it, showing somebody two of their own rows, is not
  // the tenant-wide chase list the rail item names — see the note in `destinations.ts`. So
  // this is the one screen in the product whose role branch is a claim about what the surface
  // IS rather than about what the server will do.
  //
  // The principal stays out because a chase list is about everybody riksu can name, so there
  // is nobody for it to open on. Routed without the role all the same, like the four below
  // it: an address somebody was SENT should reach riksu and be refused by riksu.
  {
    path: "/documents",
    render: ({ state }) => (
      <Documents
        roles={state.riksu.roles ?? []}
        mayPublishPolicies={holdsModule(state.riksu, "people")}
      />
    ),
  },
  // THE PERIODIC DESTRUCTION REVIEW, and the ROLES go in for the reason they go into
  // `/payroll` and `/assets`: every one of the five routes is `Subject::Role`, so the screen
  // refuses politely rather than drawing controls riksu will refuse. The PRINCIPAL does not,
  // and that is the sharper half — this module has no self-service in it at all. Nobody
  // reviews their own file for destruction, so there is nobody for this screen to open on.
  //
  // Routed without the role all the same: an address somebody was SENT should reach riksu
  // and be refused by riksu, with the sentence riksu wrote.
  {
    path: "/retention",
    render: ({ state }) => <DestinationEntrance at="/retention" state={state} />,
  },
  {
    path: "/retention/reviews",
    render: ({ state }) => <Retention roles={state.riksu.roles ?? []} />,
  },
  // The records, as a second entry rather than as `/retention/:section` alone — the pair
  // `/payroll` and `/assets` both take, and for the same reason: `ROUTE_PATHS` is what the
  // trail gate walks, and a single parameterised route would leave `/retention` itself
  // unwalked, which is the address somebody actually lands on.
  {
    path: "/retention/:section",
    render: ({ state }) => <Retention roles={state.riksu.roles ?? []} />,
  },
];
